import * as DetectBrowser from "detect-browser";
import calculateTierPrice, { calculateTierUnitPrice } from "../helpers/tierprice";
import parseUrlParams from "../helpers/urlparam";
import * as React from "react";
import DatePicker from "react-date-picker";
import * as ReactGA from "react-ga";
import { FaCalendarAlt, FaCheck, FaExclamation, FaMinus, FaPlus } from "react-icons/fa";
import { FaQuestionCircle } from "react-icons/fa";
import { Language, LocalizeContextProps, Translate, withLocalize } from "react-localize-redux";
import { Link, RouteComponentProps } from "react-router-dom";
import Slider from "rc-slider";
import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Observable, Observer, Subject, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { UncontrolledTooltip } from "reactstrap";
import ICatalogItem from "../models/Catalog";
import IOffer from "../models/Offer";
import IPrice from "../models/Price";
import ApiService from "../services/ApiService";
import OfferService from "../services/OfferService";
import "./Calculator.scss";
import FeatureTable from "./FeatureTable";
import Jumbotron from "./Jumbotron";
import Navbar from "./Navbar";
import Number from "./Number";
import PackageCard from "./PackageCard";
import PackageExtraCard from "./PackageExtraCard";
import StepperMobile from "./StepperMobile";
import "rc-slider/assets/index.css";

interface IMyState {
  catalogData: any; // All catalog data
  changePackageTooltip: boolean;
  companySize: any;
  error: boolean;
  errorMessage: string;
  extraPackages: any[];
  isLoading: boolean;
  isLoadingPrice: boolean;
  isSelectionValid: boolean;
  netvisorOpeningFee: boolean;
  offer: IOffer;
  packages: any[];
  price: IPrice;
  recommendedPackage: string;
  recommendedTraining: string;
  showOfferDialog: boolean;
  showSuccessDialog: boolean;
  showWarningDialog: boolean;
  summaryEnabled: boolean;
  token?: string | undefined; // This is the offer id when opening an existing offer
  totalPrice: number;
  trainingPackages: any[];
  tooltipPackage: string;
  salaryCalculationTrainingPackages: any[];
}

type IProps = {
  language: Language;
  languageChanged: any;
  offer: IOffer;
  price: IPrice;
  updateOffers: any;
  updateAndExpandOffer: any;
} & LocalizeContextProps &
  RouteComponentProps<{}>;

class Calculator extends React.Component<IProps, IMyState> {
  // This maps integers 1-8 to the corresponding IDs (yes, they are weird and not in order)
  private companySizeMap = {
    1: 35, // CompanySize35: "under 0.1 Meur",
    2: 1, // CompanySize1: "0.1 - 0.2 Meur",
    3: 2, // CompanySize2: "0.2 - 0.5 Meur",
    4: 17, // CompanySize17: "0.5 - 1.0 Meur",
    5: 5, // CompanySize5: "1.0 - 2.0 Meur",
    6: 6, // CompanySize6: "2.0 - 5.0 Meur",
    7: 38, // CompanySize38: "5.0 - 10.0 Meur"
    8: 7, // CompanySize7: "10 - 25 Meur",
    9: 58, // CompanySize58: "25 - 50 Meur",
    10: 59, // CompanySize59: "over 50 - 100 Meur",
    11: 61, // CompanySize61: "over 100 Meur",
  };
  private companySizeMapReverse = Object.keys(this.companySizeMap).reduce((o, key) => {
    o[this.companySizeMap[key]] = +key;
    return o;
  }, {});

  private defaultPrice = {
    oneTime: {
      price: 0,
      products: [],
    },
    price: 0,
    recurring: {
      price: 0,
      products: [],
    },
    usage: {
      price: 0,
      products: [],
    },
  };

  private api: ApiService = new ApiService();
  private description: React.RefObject<HTMLTextAreaElement> = React.createRef();
  private dialogClicks$: Subject<any> = new Subject();
  private dialogSubscription: Subscription;
  private offerDialogToken: React.RefObject<HTMLInputElement> = React.createRef();
  private offerService: OfferService = new OfferService();
  private timeout: NodeJS.Timeout;
  private updatePrice$: Subject<any> = new Subject();

  constructor(props: any) {
    super(props);

    this.updatePrice$
      .pipe(debounceTime(400))
      .subscribe((data?: { offers: IOffer[]; catalogData: ICatalogItem[]; currentToken: string }) => this.updatePrice(data ? data.offers : undefined, data ? data.catalogData : undefined, data ? data.currentToken : ""));

    const packages = [
      {
        features: ["NV-basic.Feature1", "NV-basic.Feature2", "NV-basic.Feature3", "NV-basic.Feature4"],
        sku: "NV-basic",
      },
      {
        features: ["NV-starter.Feature1", "NV-starter.Feature2", "NV-starter.Feature3"],
        sku: "NV-starter",
      },
      {
        features: ["NV-core.Feature1", "NV-core.Feature2", "NV-core.Feature3", "NV-core.Feature4"],
        sku: "NV-core",
      },
      {
        features: ["NV-prof.Feature1", "NV-prof.Feature2", "NV-prof.Feature3", "NV-prof.Feature4"],
        sku: "NV-prof",
      },
      {
        features: ["NV-nvpalkp.Feature1"],
        sku: "NV-nvpalkp",
      },
    ];

    const extraPackages = [
      { sku: "NV-pal", disabled: false },
      { sku: "NV-mkl" },
      { sku: "NV-til" },
      { sku: "NV-toi" },
      { sku: "NV-kons", type: "small", hidden: true },
      { sku: "NV-rb", type: "small", hidden: false, disabled: true },
      { sku: "NV-cont", type: "small", hidden: true },
    ];

    const trainingPackages = [
      { sku: "NV-kou1", features: ["NV-kou1.Feature1", "NV-kou1.Feature2", "NV-kou1.Feature3", "NV-kou1.Feature4", "NV-kou1.Feature5"] },
      { sku: "NV-kou2", features: ["NV-kou2.Feature1", "NV-kou2.Feature2", "NV-kou2.Feature3", "NV-kou2.Feature4", "NV-kou2.Feature5"] },
      { sku: "NV-kou3", features: ["NV-kou3.Feature1", "NV-kou3.Feature2", "NV-kou3.Feature3", "NV-kou3.Feature4", "NV-kou3.Feature5"] },
      { sku: "NV-kou5", features: ["NV-kou5.Feature1", "NV-kou5.Feature2", "NV-kou5.Feature3", "NV-kou5.Feature4", "NV-kou5.Feature5"], hidden: true },
      { sku: "NV-kou6", features: ["NV-kou6.Feature1", "NV-kou6.Feature2"], hidden: true },
    ];

    const salaryCalculationTrainingPackages = [
      { sku: "NV-palkou-basic", features: ["NV-palkou-basic.feature1", "NV-palkou-basic.feature2", "NV-palkou-basic.feature3", "NV-palkou-basic.feature4"], hidden: false },
      { sku: "NV-palkou-extended", features: ["NV-palkou-extended.feature1", "NV-palkou-extended.feature2", "NV-palkou-extended.feature3", "NV-palkou-extended.feature4", "NV-palkou-extended.feature5"], hidden: false },
    ];

    // get token from url params
    const params = parseUrlParams();
    const token = params.token;

    const netvisorOpeningFee = !!this.props.offer.selection.extraServices.find((e: any) => e.sku === "NV-net" && e.selected !== false);

    // Summary is enabled if offer has required contact information
    const summaryEnabled = !!this.props.offer.offerContact.companyName && !!this.props.offer.offerCreator.emailAddress;

    this.state = {
      catalogData: null,
      changePackageTooltip: false,
      companySize: this.companySizeMapReverse[this.props.offer.selection.companySize],
      error: false,
      errorMessage: "",
      extraPackages,
      isLoading: true,
      isLoadingPrice: false,
      isSelectionValid: !!this.props.offer.selection.product.sku,
      netvisorOpeningFee,
      offer: this.props.offer,
      packages,
      price: this.defaultPrice,
      recommendedPackage: "NV-basic",
      recommendedTraining: "",
      salaryCalculationTrainingPackages,
      showOfferDialog: false,
      showSuccessDialog: false,
      showWarningDialog: false,
      summaryEnabled,
      token,
      tooltipPackage: "",
      totalPrice: 0,
      trainingPackages,
    };

    this.addSalesAndPurchaseExtra = this.addSalesAndPurchaseExtra.bind(this);
    this.businessOrganizationChanged = this.businessOrganizationChanged.bind(this);
    this.expirationDateChanged = this.expirationDateChanged.bind(this);
    this.handleContinueButton = this.handleContinueButton.bind(this);
    this.handleDialogClose = this.handleDialogClose.bind(this);
    this.handleDialogEnter = this.handleDialogEnter.bind(this);
    this.handleExistingOffer = this.handleExistingOffer.bind(this);
    this.handleExtraQuantity = this.handleExtraQuantity.bind(this);
    this.handleExtraSelect = this.handleExtraSelect.bind(this);
    this.handleNetvisorOpeningFee = this.handleNetvisorOpeningFee.bind(this);
    this.handlePackageSelect = this.handlePackageSelect.bind(this);
    this.handlePlusMinus = this.handlePlusMinus.bind(this);
    this.handleTrainingSelect = this.handleTrainingSelect.bind(this);
    this.handleYes = this.handleYes.bind(this);
    this.onAmountChanged = this.onAmountChanged.bind(this);
    this.onCompanySizeChanged = this.onCompanySizeChanged.bind(this);
    this.toggleOfferDialog = this.toggleOfferDialog.bind(this);
  }

  public render() {
    if (this.state.isLoading) {
      return (
        <div className="App">
          <Navbar current={1} language={this.props.language} languageChanged={this.props.languageChanged} />
          <Jumbotron />
          <div className="container">
            <div className="my-5 d-flex justify-content-center">
              <span className={"spinner-border text-primary "} role="status">
                <span className="sr-only">
                  <Translate id="Loading" />
                  ...
                </span>
              </span>
            </div>
          </div>
        </div>
      );
    } else if (this.state.error && this.state.errorMessage) {
      return (
        <div className="App">
          <Navbar current={1} language={this.props.language} languageChanged={this.props.languageChanged} />
          <Jumbotron />
          <div className="container">
            <div className="row justify-content-center text-danger">{this.state.errorMessage}</div>
          </div>
        </div>
      );
    }

    return (
      <div className="App">
        <Modal isOpen={this.state.showOfferDialog} toggle={this.toggleOfferDialog} centered={true}>
          <ModalHeader toggle={this.toggleOfferDialog}>
            <Translate id="EditExistingOffer" />
          </ModalHeader>
          <ModalBody>
            <label>
              <Translate id="OfferToken" />
            </label>
            <div className="input-group">
              <input type="text" className="form-control" ref={this.offerDialogToken} onKeyUp={this.handleDialogEnter} />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button tabIndex={0} color="secondary" aria-label="Close" onClick={this.handleDialogClose}>
              <Translate id="Cancel" />
            </Button>
            <Button tabIndex={0} color="success" onClick={this.handleYes}>
              <Translate id="OK" />
            </Button>
          </ModalFooter>
        </Modal>
        <Alert color="danger" isOpen={this.state.showWarningDialog} toggle={this.handleDialogClose}>
          <span className="check mr-3">
            <FaExclamation />
          </span>
          <Translate id="OfferCouldNotBeOpened" />
        </Alert>
        <Alert color="success" isOpen={this.state.showSuccessDialog} toggle={this.handleDialogClose}>
          <span className="check mr-3">
            <FaCheck />
          </span>
          <Translate id="OfferSuccessfullyOpened" />
        </Alert>
        <Navbar current={1} summaryEnabled={this.state.summaryEnabled} language={this.props.language} languageChanged={this.props.languageChanged} onPreviousOffer={this.handleExistingOffer} />
        <Jumbotron onPreviousOffer={this.handleExistingOffer} />
        <div className="container my-3">
          <StepperMobile current={1} summaryEnabled={this.state.summaryEnabled} />
          <div className="my-4">
            <div className="my-2">
              {process.env.REACT_APP_FE_MODE === "embed" ? null : (
                <div className="row mt-5 mb-3">
                  <div className="col-12 col-md-auto pr-0">
                    <label className="custom-radio">
                      <Translate id="Company" />
                      <input type="radio" value={1} checked={this.state.offer.selection.businessOrganization === 1} onChange={this.businessOrganizationChanged} />
                      <span className="checkmark" />
                    </label>
                  </div>
                  <div className="col-12 col-md-auto pr-0">
                    <label className="custom-radio">
                      <Translate id="GroupCompany" />
                      <input type="radio" value={2} checked={this.state.offer.selection.businessOrganization === 2} onChange={this.businessOrganizationChanged} />
                      <span className="checkmark" />
                    </label>
                  </div>
                  {/* 
                  <div className="col-12 col-md-auto pr-0">
                    <label className="custom-radio">
                      <Translate id="Association" />
                      <input type="radio" value={3} checked={this.state.offer.selection.businessOrganization === 3} onChange={this.businessOrganizationChanged} />
                      <span className="checkmark" />
                    </label>
                  </div>
				  */}
                </div>
              )}
              {process.env.REACT_APP_FE_MODE === "embed" && (
                <div className="base-note">
                  <h6>
                    <Translate id="NoteBaseTitle" />
                  </h6>
                  <ul>
                    <li>
                      <Translate id="NoteBaseItem01" />
                    </li>
                    <li>
                      <Translate id="NoteBaseItem02" />
                    </li>
                    <li>
                      <Translate id="NoteBaseItem03" />
                    </li>
                  </ul>
                </div>
              )}
              <div className="row align-items-center mx-0">
                <h3 className="w-100 mt-5">
                  1. <Translate id="CompanySizeHeader" />
                </h3>
                <span className="text-secondary">
                  <Translate id="CompanySizeDescription" />
                </span>
              </div>
              <div className="row mt-4">
                <div className="col-12 col-md-6">
                  <div className="d-flex flex-wrap justify-content-between align-items-baseline">
                    <div className="col-12 col-sm-auto d-flex align-items-center mb-4 p-0">
                      <span>
                        <Translate id="CompanySize" />
                      </span>
                      <span className="help-tooltip d-flex" id="companySizeTooltip">
                        <FaQuestionCircle />
                      </span>
                      <UncontrolledTooltip placement="right" target="companySizeTooltip" autohide={false}>
                        {this.props.translate("CompanySizeDesc").toString()}
                      </UncontrolledTooltip>
                    </div>
                    <h5 className="p-0 col-12 col-sm-auto">
                      <Translate id={"CompanySize" + this.state.offer.selection.companySize} />
                    </h5>
                  </div>
                  <div className="px-3 px-sm-0 mb-4">
                    <Slider value={this.state.companySize} min={1} max={11} onChange={this.onCompanySizeChanged} />
                  </div>
                </div>
              </div>
              <div className="row align-items-center my-4 mb-5 mx-0">
                <h3 className="w-100 mt-5">
                  2. <Translate id="ChoosePackage" />
                </h3>
                <span className="text-secondary">
                  <Translate id="PackageDesc" />
                </span>
              </div>
              <div className="packages row d-flex flex-wrap my-3 mt-5">
                {this.state.packages.map((p, i) => {
                  if (p.sku !== "NV-nvpalkp") {
                    return (
                      <div className="col-12 col-md-6 col-lg-4 col-xl-3 p-3" key={i}>
                        <PackageCard package={p} selected={this.state.offer.selection.product.sku === p.sku} packageSelected={this.handlePackageSelect} recommended={this.state.recommendedPackage === p.sku} />
                      </div>
                    );
                  } else {
                    return (
                      <div className="col-12 col-md-6 col-lg-4 col-xl-3 p-3" key={i}>
                        <PackageExtraCard
                          package={p}
                          selected={this.state.offer.selection.product.sku === p.sku}
                          type={p.type || "large"}
                          hasInput={true}
                          packageSelected={this.handlePackageSelect}
                          quantityChanged={this.handleExtraQuantity}
                          recommended={this.state.recommendedPackage === p.sku}
                        />
                      </div>
                    );
                  }
                })}
              </div>
              <FeatureTable catalogData={this.state.catalogData} />
            </div>
            <div className="row align-items-center mt-5 mx-0 ">
              <h3 className="w-100 mt-5">
                3. <Translate id="Extras" />
              </h3>
              <span className="text-secondary">
                <Translate id="ExtrasDesc" />
              </span>
            </div>
            <div className="my-2">
              <div className="packages row d-flex flex-wrap">
                {this.state.extraPackages
                  .filter((p: any) => p.type !== "small" && p.sku !== "NV-til" && p.sku !== "NV-toi")
                  .map((p, i) => {
                    return (
                      <div className="col-12 p-3 col-md-6 col-lg-4 col-xl-3" key={i}>
                        <PackageExtraCard
                          package={p}
                          selected={!!this.state.offer.selection.extraServices.find((s: any) => s.sku === p.sku && s.selected)}
                          type={p.type || "large"}
                          hasInput={true}
                          packageSelected={this.handleExtraSelect}
                          quantityChanged={this.handleExtraQuantity}
                        />
                      </div>
                    );
                  })}
                <div className="d-flex flex-column col-12 col-lg-6 p-0">
                  {this.state.extraPackages
                    .filter((p: any) => p.type === "small" && !p.hidden)
                    .map((p, i) => {
                      return (
                        <div className="p-3" key={i}>
                          <PackageExtraCard
                            package={p}
                            selected={!!this.state.offer.selection.extraServices.find((s: any) => s.sku === p.sku && s.selected)}
                            type={p.type || "large"}
                            hasInput={true}
                            packageSelected={this.handleExtraSelect}
                            quantityChanged={this.handleExtraQuantity}
                          />
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
            <div className="row align-items-center mx-0 mt-5">
              <h3 className="w-100 mt-5">
                4. <Translate id={(this.state.offer.selection.product.sku !== "NV-starter") ? "PaymentHeader" : "StarterPaymentHeader"} />
              </h3>
              <span className="text-secondary">
                <Translate id={(this.state.offer.selection.product.sku !== "NV-starter") ? "PaymentDesc" : "StarterPaymentDesc"} />
              </span>
            </div>
            {this.state.offer.selection.product.sku !== "NV-starter" && (
            <div className="row mt-4">
              <div className={"col-12 col-md-4 col-xl-3" + (this.state.offer.selection.sales[0].disabled ? " disabled" : "")}>
                <div className="col-12 col-sm-auto d-flex align-items-center p-0 mb-4 mt-4 mt-md-0">
                  <span>
                    <Translate id="SalesAmount" />
                  </span>
                  <span className="help-tooltip d-flex" id="salesAmountTooltip">
                    <FaQuestionCircle />
                  </span>
                  {!this.state.offer.selection.sales[0].disabled ? (
                    <UncontrolledTooltip placement="right" target="salesAmountTooltip" autohide={false}>
                      {this.props.translate("SalesAmountDesc").toString()}
                    </UncontrolledTooltip>
                  ) : null}
                </div>
                <div className="input-group">
                  <span className="input-group-btn input-group-prepend">
                    <Button tabIndex={0} className="text-primary btn btn-default btn-number" data-type="minus" data-property="sales" disabled={this.state.offer.selection.sales[0].disabled ? true : false} onClick={this.handlePlusMinus}>
                      <FaMinus size="1.5em" />
                    </Button>
                  </span>
                  <input
                    type="number"
                    className="form-control input-number text-center"
                    disabled={this.state.offer.selection.sales[0].disabled ? true : false}
                    value={this.state.offer.selection.sales[0].quantity}
                    min="0"
                    max="100000"
                    data-property="sales"
                    onChange={this.onAmountChanged}
                    onFocus={this.handleFocus}
                  />
                  <span className="input-group-btn input-group-append">
                    <Button tabIndex={0} className="text-primary btn btn-default btn-number" data-type="plus" data-property="sales" disabled={this.state.offer.selection.sales[0].disabled ? true : false} onClick={this.handlePlusMinus}>
                      <FaPlus size="1.5em" />
                    </Button>
                  </span>
                </div>
                <div className="my-3 font-weight-bold">
                  <div>
                    <Translate id="PriceEstimation" />: <Number value={this.state.price.sales || NaN} /> <Translate id="EuroPerMonth" />
                  </div>
                </div>
              </div>
              <div className={"col-12 col-md-4 col-xl-3" + (this.state.offer.selection.purchases[0].disabled ? " disabled" : "")}>
                <div className="col-12 col-sm-auto d-flex align-items-center p-0 mb-4 mt-4 mt-md-0">
                  <span>
                    <Translate id="PurchaseAmount" />
                  </span>
                  <span className="help-tooltip d-flex" id="PurchaseAmountTooltip">
                    <FaQuestionCircle />
                  </span>
                  {!this.state.offer.selection.purchases[0].disabled ? (
                    <UncontrolledTooltip placement="right" target="PurchaseAmountTooltip" autohide={false}>
                      {this.props.translate("PurchaseAmountDesc").toString()}
                    </UncontrolledTooltip>
                  ) : null}
                </div>
                <div className="input-group">
                  <span className="input-group-btn input-group-prepend">
                    <Button tabIndex={0} className="text-primary btn btn-default btn-number" data-type="minus" data-property="purchases" disabled={this.state.offer.selection.purchases[0].disabled ? true : false} onClick={this.handlePlusMinus}>
                      <FaMinus size="1.5em" />
                    </Button>
                  </span>
                  <input
                    type="number"
                    className="form-control input-number text-center"
                    disabled={this.state.offer.selection.purchases[0].disabled ? true : false}
                    value={this.state.offer.selection.purchases[0].quantity}
                    min="0"
                    max="100000"
                    data-property="purchases"
                    onChange={this.onAmountChanged}
                    onFocus={this.handleFocus}
                  />
                  <span className="input-group-btn input-group-append">
                    <Button tabIndex={0} className="text-primary btn btn-default btn-number" data-type="plus" data-property="purchases" disabled={this.state.offer.selection.purchases[0].disabled ? true : false} onClick={this.handlePlusMinus}>
                      <FaPlus size="1.5em" />
                    </Button>
                  </span>
                </div>
                <div className="my-3 font-weight-bold">
                  <div>
                    <Translate id="PriceEstimation" />: <Number value={this.state.price.purchases || NaN} /> <Translate id="EuroPerMonth" />
                  </div>
                </div>
              </div>
              {this.state.extraPackages
                .filter((p: any) => p.sku === "NV-til")
                .map((p, i) => {
                  return (
                    <div className={"col-12 col-md-4 col-xl-3" + (p.disabled ? " disabled" : "")} key={i}>
                      <div className="col-12 col-sm-auto d-flex align-items-center p-0 mb-4 mt-4 mt-md-0">
                        <span>
                          <Translate id="BankSearch" />
                        </span>
                        <span className="help-tooltip d-flex" id="BankSearchTooltip">
                          <FaQuestionCircle />
                        </span>
                        {!p.disabled ? (
                          <UncontrolledTooltip placement="right" target="BankSearchTooltip" autohide={false}>
                            {this.props.translate("BankSearchDesc").toString()}
                          </UncontrolledTooltip>
                        ) : null}
                      </div>
                      <div className="input-group">
                        <span className="input-group-btn input-group-prepend">
                          <Button tabIndex={0} className="text-primary btn btn-default btn-number" data-type="minus" data-property="accounts" disabled={p.disabled ? true : false} onClick={this.handlePlusMinus}>
                            <FaMinus size="1.5em" />
                          </Button>
                        </span>
                        <input
                          type="number"
                          className="form-control input-number text-center"
                          disabled={p.disabled ? true : false}
                          value={p.quantity}
                          min="0"
                          max="100000"
                          data-property="accounts"
                          onChange={this.onAmountChanged}
                          onFocus={this.handleFocus}
                        />
                        <span className="input-group-btn input-group-append">
                          <Button tabIndex={0} className="text-primary btn btn-default btn-number" data-type="plus" data-property="accounts" disabled={p.disabled ? true : false} onClick={this.handlePlusMinus}>
                            <FaPlus size="1.5em" />
                          </Button>
                        </span>
                      </div>
                      <div className="my-3 font-weight-bold">
                        <div>
                          <Translate id="PriceEstimation" />: <Number value={this.state.extraPackages[2].price || NaN} /> <Translate id="EuroPerMonth" />
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
            )}
            {this.state.changePackageTooltip && (
              <div className="tooltip-orange row mt-0 mb-5 ml-1" id="ChangePackageTooltip">
                <span className="tooltip-text">
                  <Translate id="Change" /> <Translate id={this.state.tooltipPackage + ".name"} />
                  <Translate id="ChangePackage" />
                </span>
              </div>
            )}
            <div className="row align-items-center mx-0 mt-5">
              <h3 className="w-100 mt-5">
                5. <Translate id="Training" />
              </h3>
              <span className="text-secondary">
                <Translate id="TrainingDesc" />
              </span>
            </div>
            <div className="my-2">
              <div className="packages row d-flex flex-wrap">
                {this.state.trainingPackages
                  .filter((p: any) => !p.hidden)
                  .map((p, i) => {
                    return (
                      <div className="col-12 col-md-6 col-lg-4 col-xl-3 p-3" key={i}>
                        <PackageExtraCard
                          package={p}
                          selected={!!(this.state.offer.selection.trainings || []).find((t: any) => t.sku === p.sku)}
                          priceUnit="EuroPerOneTime"
                          packageSelected={this.handleTrainingSelect}
                          recommended={this.state.recommendedTraining === p.sku}
                        />
                      </div>
                    );
                  })}
                {this.state.salaryCalculationTrainingPackages
                  .filter((p: any) => !p.hidden)
                  .map((p, i) => {
                    return (
                      <div className="col-12 col-md-6 col-lg-4 col-xl-3 p-3" key={i}>
                        <PackageExtraCard
                          package={p}
                          selected={!!(this.state.offer.selection.trainings || []).find((t: any) => t.sku === p.sku)}
                          priceUnit="EuroPerOneTime"
                          packageSelected={this.handleTrainingSelect}
                          recommended={this.state.recommendedTraining === p.sku}
                        />
                      </div>
                    );
                  })}
              </div>
            </div>
            {process.env.REACT_APP_FE_MODE !== "embed" && (
              <div>
                <div className="row align-items-center mx-0 mt-5">
                  <h3 className="w-100 mt-5">
                    6. <Translate id="Additional" />
                  </h3>
                  <span className="text-secondary">
                    <Translate id="AdditionalDesc" />
                  </span>
                </div>
                <div className="my-3">
                  <div className="my-3">
                    <Translate id="OfferExpirationDate" />
                  </div>
                  <DatePicker className="mb-2" locale="fi-FI" value={this.state.offer.expirationUTC} onChange={this.expirationDateChanged} calendarIcon={<FaCalendarAlt />} />
                  <div className="my-3">
                    <Translate id="AdditionalText" />
                  </div>
                  <textarea className="form-control" rows={3} defaultValue={this.state.offer.description} ref={this.description} />
                  {process.env.REACT_APP_FE_MODE === "internal" && (
                    <div className="align-items-center row my-3">
                      <div className="col-auto">
                        <label className="switch mb-0">
                          <input type="checkbox" checked={this.state.netvisorOpeningFee} onChange={this.handleNetvisorOpeningFee} />
                          <span className="slider round" />
                        </label>
                      </div>
                      <span className="col">
                        <Translate id="NetvisorOpeningFee" />
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {this.state.isSelectionValid && (
            <div>
              <hr className="darker" />
              <div className="my-4">
                <h3 className="w-100 mt-5">
                  <Translate id="Summary" />
                </h3>
                <div className="my-4">
                  <div className="row justify-content-between">
                    <div className="col-3 d-none d-sm-block font-weight-bold">
                      <Translate id="Package" />
                    </div>
                    <div className="col-9 col-sm-6">{this.state.offer.selection.product.sku ? <Translate id={this.state.offer.selection.product.sku + ".name"} /> : null}</div>
                    <div className="col text-right">
                      {/* <Number value={this.state.packages[this.state.offer.selection.product.sku].price || NaN} /> <Translate id="EuroPerMonth" /> */}
                      {(this.state.packages || [])
                        .filter((e) => e.sku === this.state.offer.selection.product.sku)
                        .map((openingFee, idx) => {
                          return (
                            <span key={idx}>
                              <Number value={openingFee.price} /> <Translate id="EuroPerMonth" />
                            </span>
                          );
                        })}
                      {/* {(this.state.packages || [])
                        .filter(e => e.sku === this.state.offer.selection.product.sku)
                        .map((openingFee, idx) => {
                          return (
                            <span key={idx}>
                              <Number value={openingFee.price} /> <Translate id="EuroPerMonth" />
                            </span>
                          );
                        })} */}
                    </div>
                    <div className="w-100" />
                  </div>
                  {(this.state.price.usage.products || []).filter((s) => this.state.isSelectionValid !== false && (s.sku === "NV-mkl" || s.sku === "NV-pal" || s.sku === "NV-kons" || s.sku === "NV-rb")).length ? <hr /> : null}
                  {(this.state.price.usage.products || [])
                    .filter((s) => this.state.isSelectionValid !== false && (s.sku === "NV-mkl" || s.sku === "NV-pal"))
                    .map((s: any, idx: number) => {
                      return (
                        <div key={idx} className="row mt-2 justify-content-between">
                          <div className="col-3 d-none d-sm-block font-weight-bold">{idx === 0 ? <Translate id="ExtraServices" /> : ""}</div>
                          <div className="col-9 col-sm-6">
                            <Translate id={s.sku + ".name"} />{" "}
                            {s.sku === "NV-mkl" ? (
                              <span>
                                <Translate>
                                  {({ translate }) => {
                                    return (
                                      <span>
                                        ({this.state.offer.selection.extraServices[1].quantity} {translate(s.sku + ".unit", undefined, { onMissingTranslation: () => "" + translate("Pieces") })})
                                      </span>
                                    );
                                  }}
                                </Translate>
                                , <Translate id="BasedOnActual" />
                              </span>
                            ) : null}
                            {s.sku === "NV-pal" ? (
                              <span>
                                <Translate>
                                  {({ translate }) => {
                                    return (
                                      <span>
                                        ({this.state.offer.selection.extraServices[0].quantity} {translate(s.sku + ".unit", undefined, { onMissingTranslation: () => "" + translate("Pieces") })})
                                      </span>
                                    );
                                  }}
                                </Translate>
                              </span>
                            ) : null}
                          </div>
                          <div className="col text-right">
                            <Number value={s.price} /> <Translate id="EuroPerMonth" />
                          </div>
                          <div className="w-100" />
                        </div>
                      );
                    })}
                  {(this.state.offer.selection.extraServices || [])
                    .filter((s) => this.state.isSelectionValid !== false && (s.sku === "NV-kons" || s.sku === "NV-rb") && s.selected === true)
                    .map((s: any, idx: number) => {
                      return (
                        <div key={idx} className="row mt-2 justify-content-between">
                          <div className="col-3 d-none d-sm-block font-weight-bold">&nbsp;</div>
                          <div className="col-9 col-sm-6">
                            + <Translate id={s.sku + ".name"} />{" "}
                            {/* <span>
                              <Translate>
                                {({ translate }) => {
                                  return (
                                    <span>
                                      ({this.state.extraPackages[2].price} {translate(s.sku + ".unit", undefined, { onMissingTranslation: () => "" + translate("Pieces") })})
                                    </span>
                                  );
                                }}
                              </Translate>
                              </span> */}
                          </div>
                          <div className="col text-right">
                            <Number value={s.price} /> <Translate id="EuroPerMonth" />
                          </div>
                          <div className="w-100" />
                        </div>
                      );
                    })}
                  <hr />
                  <div className="row mt-2 justify-content-between">
                    <div className="col-3 d-none d-sm-block font-weight-bold">
                      <Translate id="UsageFee" />
                    </div>
                    <div className="col-9 col-sm-6">
                      <div className="font-italic mb-2 d-none d-sm-block">
                        <Translate id="PriceEstimation" />, <Translate id="BasedOnActual" />
                      </div>
                    </div>
                    <div className="col text-right">&nbsp;</div>
                  </div>
                  {(this.state.price.usage.products || [])
                    .filter((s) => this.state.isSelectionValid !== false && (s.sku === "NV-tr-ol" || s.sku === "NV-tr-ml"))
                    .map((s: any, idx: number) => {
                      return (
                        <div key={idx} className="row mt-2 justify-content-between">
                          <div className="col-3 d-none d-sm-block font-weight-bold">&nbsp;</div>
                          <div className="col-9 col-sm-6">
                            <Translate id={s.sku + ".name"} />{" "}
                          </div>
                          <div className="col text-right">
                            <Number value={s.price} /> <Translate id="EuroPerMonth" />
                          </div>
                        </div>
                      );
                    })}
                  {(this.state.price.usage.products || [])
                    .filter((s) => this.state.isSelectionValid !== false && (s.sku === "NV-vlv" || s.sku === "NV-vll" || s.sku === "NV-verk-p" || s.sku === "NV-tri"))
                    .map((s: any, idx: number) => {
                      return (
                        <div key={idx} className="row mt-2 justify-content-between">
                          <div className="col-3 d-none d-sm-block font-weight-bold" />
                          <div className="col-9 col-sm-6">
                            {idx === 0 ? (
                              <div className="mb-2">
                                <Translate id="ElectronicActions" />
                              </div>
                            ) : (
                              ""
                            )}
                            <div className="ml-4">
                              <Translate id={s.sku + ".name"} />{" "}
                            </div>
                          </div>
                          <div className="col text-right">
                            {idx === 0 ? <div className="font-italic mb-2">&nbsp;</div> : ""}
                            <Number value={s.price} /> <Translate id="EuroPerMonth" />
                          </div>
                        </div>
                      );
                    })}
                  {(this.state.price.usage.products || [])
                    .filter((s) => this.state.isSelectionValid !== false && (s.sku === "NV-vii" || s.sku === "NV-mak" || s.sku === "NV-til" || s.sku === "NV-toi"))
                    .map((s: any, idx: number) => {
                      return (
                        <div key={idx} className="row mt-2 justify-content-between">
                          <div className="col-3 d-none d-sm-block font-weight-bold" />
                          <div className="col-9 col-sm-6">
                            {idx === 0 ? (
                              <div className="mb-2">
                                <Translate id="BankingActions" />
                              </div>
                            ) : (
                              ""
                            )}
                            <div className="ml-4">
                              <Translate id={s.sku + ".name"} />{" "}
                            </div>
                          </div>
                          <div className="col text-right">
                            {idx === 0 ? <div className="font-italic mb-2">&nbsp;</div> : ""}
                            <Number value={s.price} /> <Translate id="EuroPerMonth" />
                          </div>
                        </div>
                      );
                    })}
                  <div className="row justify-content-between font-weight-bold bg-light pt-3 pb-1 my-3">
                    <h6 className="col-auto font-weight-bold text-uppercase">
                      <Translate id="PackageAndExtras" />
                    </h6>
                    <div className="col-auto text-right">
                      <Number value={this.state.totalPrice} /> <Translate id="EuroPerMonth" />
                    </div>
                  </div>
                  {(this.state.price.usage.products || [])
                    .filter((e) => this.state.isSelectionValid !== false && e.sku === "NV-net")
                    .map((openingFee, idx) => {
                      return (
                        <div key={idx}>
                          <div className="row justify-content-between">
                            <div className="col-auto col-sm-3 font-weight-bold">
                              <span>
                                <Translate id={openingFee.sku + ".name"} />
                              </span>
                            </div>
                            <div className="col-6 d-none d-sm-block" />
                            <div className="col text-right">
                              + <Number value={openingFee.price || 0} /> €
                            </div>
                            <div className="w-100" />
                          </div>
                          <hr />
                        </div>
                      );
                    })}
                  {(this.state.price.oneTime.products || []).map((training: any, idx: number, arr: any[]) => {
                    return (
                      <div key={idx}>
                        <div className="row justify-content-between mt-2">
                          <div className={"font-weight-bold col-3 d-none " + (idx === 0 ? "d-sm-block" : "")}>
                            <Translate id="TrainingPackages" />
                          </div>
                          <div className={"col-6 " + (idx > 0 ? "offset-sm-3" : "")}>
                            <Translate id={training.sku + ".name"} />{" "}
                          </div>
                          <div className="col text-right">
                            + <Number value={training.price || 0} /> €
                          </div>
                          <div className="w-100" />
                        </div>
                        {idx === arr.length - 1 ? <hr /> : null}
                      </div>
                    );
                  })}
                  <div className="my-4 row">
                    <div className="col text-right small">
                      <Translate id="Prices" /> <Translate id="Tax0" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <hr className="darker" />
          <div className="row">
            <div className="col-6 d-flex mb-3 mt-5">
              {process.env.REACT_APP_FE_MODE === "embed" && (
                <Link to="/contactRequest" tabIndex={-1} onClick={this.handleContinueButton}>
                  <span className="mx-2 font-weight-bold">
                    <Translate id="ContactRequest" />
                  </span>
                </Link>
              )}
            </div>
            <div className="col-6 mt-5 d-flex justify-content-end">
              <Link to="/summary" tabIndex={-1} onClick={this.handleContinueButton}>
                <Button tabIndex={0} className="btn btn-primary btn-success" disabled={!this.state.isSelectionValid}>
                  <Translate id="ContinueToSummary" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  public componentDidMount() {
    this.setState({ isLoading: true });

    // Get current offer if we have token in the url params
    const offer$ = Observable.create((observer: Observer<any>) => {
      if (this.state.token && this.state.token && !/^\w{8}-\w{4}-\w{4}-\w{4}-\w{12}$/.test(this.state.token)) {
        this.api.getOffers(this.state.token).subscribe(
          (offers: IOffer[]) => {
            const foundOffer = offers.find((offer: IOffer) => offer.token === this.state.token);
            if (foundOffer) {
              this.preprocessOffer(foundOffer);
              this.setState({
                companySize: this.companySizeMapReverse[foundOffer.selection.companySize],
                isSelectionValid: !!foundOffer.selection.product.sku,
                netvisorOpeningFee: !!foundOffer.selection.extraServices.find((e: any) => e.sku === "NV-net"),
                offer: foundOffer,
              });
              observer.next({ offers, currentToken: foundOffer.token });
              observer.complete();
            }
          },
          (error) => {
            observer.error(error);
          }
        );
      } else {
        observer.next("");
        observer.complete();
      }
    });

    // Get the catalog as soon as component mounted
    offer$.subscribe((data: { offers: IOffer[]; currentToken: string }) => {
      this.api.getCatalog().subscribe(
        (catalogData: ICatalogItem[]) => {
          this.setState(
            {
              catalogData,
              extraPackages: this.calculateExtraPrices(catalogData, this.state.offer.selection),
              isLoading: false,
              packages: this.calculatePackagePrices(catalogData, this.state.offer.selection.companySize, this.state.offer.selection.extraServices),
              salaryCalculationTrainingPackages: this.calculateSalaryCalculationTrainingPrices(catalogData, this.state.offer.selection),
              trainingPackages: this.calculateTrainingPrices(catalogData, this.state.offer.selection, this.props.offer.selection.product.sku),
            },
            () =>
              this.updatePrice$.next({
                catalogData,
                currentToken: data.currentToken,
                offers: data.offers,
              })
          );

          // Activate tooltips
          ($('[data-toggle="tooltip"]') as any).tooltip();
        },
        (error) => {
          console.error("Error when getting catalog!", error);
          this.setState({
            catalogData: null,
            error: true,
            errorMessage: error.toString(),
            isLoading: false,
          });
        }
      );
    });
  }

  public componentWillUnmount() {
    ($('[data-toggle="tooltip"]') as any).tooltip("hide");

    clearTimeout(this.timeout);
  }

  private calcTotalPrice() {
    let curPrice = 0;
    let finalPrice = 0;

    if (this.state.price.usage.products) {
      this.state.price.usage.products.forEach((a) => {
        if (
          a.sku === "NV-tr-ml" ||
          a.sku === "NV-tr-ol" ||
          a.sku === "NV-mkl" ||
          a.sku === "NV-pal" ||
          a.sku === "NV-til" ||
          a.sku === "NV-toi" ||
          a.sku === "NV-vlv" ||
          a.sku === "NV-mak" ||
          a.sku === "NV-vll" ||
          a.sku === "NV-vii" ||
          a.sku === "NV-verk-p" ||
          a.sku === "NV-tri"
        ) {
          curPrice = curPrice + a.price;
        }
      });
      finalPrice = this.state.price.recurring.price + curPrice;
    }
    return finalPrice;
  }
  private preprocessOffer(processOffer: IOffer) {
    // convert expiration to date
    processOffer.expirationUTC = new Date(processOffer.expirationUTC || "");
    const tilExtra = processOffer.selection.extraServices.findIndex((extra) => extra.sku === "NV-til");
    if (tilExtra === -1) {
      processOffer.selection.extraServices.push({
        price: 0,
        quantity: 0,
        selected: false,
        sku: "NV-til",
      });
    }
    let tilQuantity;
    if (processOffer.selection.extraServices[tilExtra]) {
      tilQuantity = processOffer.selection.extraServices[tilExtra].quantity;
    }
    if (tilExtra > -1 && tilQuantity) {
      processOffer.selection.extraServices[tilExtra].quantity = tilQuantity / 21;
    }
    if (processOffer.selection.product.sku === "NV-nvpalkp") {
      const palExtra = processOffer.selection.extraServices.findIndex((extra) => extra.sku === "NV-pal");
      if (palExtra === -1) {
        processOffer.selection.extraServices.push({
          price: 0,
          quantity: 0,
          selected: false,
          sku: "NV-pal",
        });
      }
      const toiExtra = processOffer.selection.extraServices.findIndex((extra) => extra.sku === "NV-toi");
      if (toiExtra === -1) {
        processOffer.selection.extraServices.push({
          price: 0,
          quantity: 0,
          selected: false,
          sku: "NV-toi",
        });
      }
      processOffer.selection.purchases.map((p, i) => {
        const foundPurchases = {
          disabled: true,
          price: 0,
          quantity: 0,
          sku: p.sku,
        };
        processOffer.selection.purchases[i] = foundPurchases;
      });
      processOffer.selection.sales.map((p, i) => {
        const foundPurchases = {
          disabled: true,
          price: 0,
          quantity: 0,
          sku: p.sku,
        };
        processOffer.selection.sales[i] = foundPurchases;
      });
      const foundSalaryExtra = processOffer.selection.extraServices.find((extra: any) => extra.sku === "NV-pal");
      if (foundSalaryExtra && foundSalaryExtra.quantity && foundSalaryExtra.quantity > 0) {
        this.setState({
          offer: processOffer,
        });
      }
    }
    // set all extras as selected (if they're on the offer, they must be selected)
    processOffer.selection.extraServices.forEach((s: any) => (s.selected = true));
    // Clean up stale training selections from removed packages
    if (processOffer.selection.trainings) {
      processOffer.selection.trainings = processOffer.selection.trainings.filter((t: any) => t.sku !== "NV-kou4");
      // If product is NV-nvpalkp, remove NV-kouX trainings; otherwise remove NV-palkou trainings
      if (processOffer.selection.product.sku === "NV-nvpalkp") {
        processOffer.selection.trainings = processOffer.selection.trainings.filter(
          (t: any) => !["NV-kou1", "NV-kou2", "NV-kou3"].includes(t.sku)
        );
      } else {
        processOffer.selection.trainings = processOffer.selection.trainings.filter(
          (t: any) => !this.state.salaryCalculationTrainingPackages.some((s: any) => s.sku === t.sku)
        );
      }
    }
    // set description value
    if (this.description.current) {
      this.description.current.value = processOffer.description || "";
    }
  }

  // This function will find the prices for packages in section 1 based on companysize
  private calculatePackagePrices(catalogData: any, companySize: number, extraServices?: IOffer["selection"]["extraServices"], curSku?: string) {
    const newPackages = Array();
    this.state.packages.forEach((p) => {
      const foundPackage = catalogData.find((dataPackage: any) => dataPackage.sku === p.sku);
      if (foundPackage) {
        const foundRatePlan = foundPackage.productRatePlans.find((plan: any) => +plan.NVCompanySize__c === +companySize);
        let calculatedPrice = 0;
        let calculatedSalaryPrice = 0;
        let salaryExtraPrice = 0;
        let salaryQuantity;
        if (extraServices) {
          const currentQuantity = extraServices.find((extra: any) => extra.sku === "NV-pal");
          if (currentQuantity) {
            salaryQuantity = currentQuantity.quantity;
          }
        }
        if (foundRatePlan) {
          calculatedPrice = foundRatePlan.productRatePlanCharges[0].pricing[0].price;
        } else if (p.sku === "NV-nvpalkp") {
          const curSalaryPrice = this.state.offer.selection.product.price;
          if (curSku === "NV-nvpalkp" || this.state.offer.selection.product.sku === "NV-nvpalkp") {
            if (curSalaryPrice && this.state.offer.selection.product.sku === "NV-nvpalkp") {
              calculatedSalaryPrice = curSalaryPrice;
            } else {
              if (salaryQuantity && salaryQuantity > 0) {
                calculatedSalaryPrice = p.price;
              } else {
                calculatedSalaryPrice = foundPackage.productRatePlans[0].productRatePlanCharges[0].pricing[0].price;
              }
            }
            salaryExtraPrice = calculatedSalaryPrice / (salaryQuantity || 1);
          } else {
            salaryExtraPrice = 0;
            calculatedSalaryPrice = foundPackage.productRatePlans[0].productRatePlanCharges[0].pricing[0].price;
            salaryQuantity = 0;
          }
        }

        // In basic package, add extra sale and purchase costs
        if (foundPackage.sku === "NV-basic" && calculatedPrice > 0 && !!extraServices) {
          const foundCatalogSales = catalogData.find((catalogPackage: any) => catalogPackage.sku === "NV-tr-ml");
          if (foundCatalogSales) {
            const foundSalesExtra = extraServices.find((extra: any) => extra.sku === "NV-tr-ml");
            if (foundSalesExtra && foundSalesExtra.quantity) {
              calculatedPrice += foundCatalogSales.productRatePlans[0].productRatePlanCharges[0].pricing[0].price * foundSalesExtra.quantity;
            }
          }
          const foundCatalogPurchases = catalogData.find((catalogPackage: any) => catalogPackage.sku === "NV-tr-ol");
          if (foundCatalogPurchases) {
            const foundPurchasesExtra = extraServices.find((extra: any) => extra.sku === "NV-tr-ol");
            if (foundPurchasesExtra && foundPurchasesExtra.quantity) {
              calculatedPrice += foundCatalogPurchases.productRatePlans[0].productRatePlanCharges[0].pricing[0].price * foundPurchasesExtra.quantity;
            }
          }
        }
        if (foundPackage.sku === "NV-nvpalkp") {
          newPackages.push({
            disabled: !calculatedSalaryPrice,
            extraPrice: salaryExtraPrice,
            extraUnit: this.props.translate("EuroPerPerson"),
            features: p.features,
            price: calculatedSalaryPrice,
            quantity: salaryQuantity,
            sku: foundPackage.sku,
            tooltipText: this.props.translate("PaymentsDesc"),
            unit: this.props.translate("Employees"),
          });
        }
        else if(foundPackage.sku === "NV-starter") {
          newPackages.push({
            disabled: !calculatedPrice,
            features: p.features,
            price: calculatedPrice,
            sku: foundPackage.sku,
            tooltipText: this.props.translate("StarterPackageTooltip"),
          });
        }
        else {
          newPackages.push({
            disabled: !calculatedPrice,
            features: p.features,
            price: calculatedPrice,
            sku: foundPackage.sku,
          });
        }
      }
    });
    return newPackages;
  }

  private calculateSalaryPackagePrice(catalogData: any, selection: any) {
    const newExtras = Array();
    this.state.packages.forEach((p) => {
      if (p.sku === "NV-nvpalkp") {
        const foundPackage = catalogData.find((dataPackage: any) => dataPackage.sku === "NV-pal");
        const nvpalkpCatalog = catalogData.find((dataPackage: any) => dataPackage.sku === "NV-nvpalkp");
        const basePrice = nvpalkpCatalog && nvpalkpCatalog.productRatePlans[0] ? nvpalkpCatalog.productRatePlans[0].productRatePlanCharges[0].pricing[0].price : 0;
        if (foundPackage && foundPackage.productRatePlans && foundPackage.productRatePlans[0] && foundPackage.productRatePlans[0] && foundPackage.productRatePlans[0].productRatePlanCharges[0].pricing[0]) {
          const charge = foundPackage.productRatePlans[0].productRatePlanCharges[0];
          const pricingTiers = charge.pricing[0].tiers;
          let totalPrice = 0;
          let extraPrice = 0; // this is used to show average price per person
          const foundExtra = selection.extraServices.find((e: any) => e.sku === foundPackage.sku);
          let extraQuantity = 0;
          if (foundExtra) {
            extraQuantity = p.quantity;
          }
          if (pricingTiers) {
            totalPrice = calculateTierPrice(charge.model, charge.pricing[0], extraQuantity);
            extraPrice = (totalPrice + basePrice) / extraQuantity || 0;
          } else {
            const foundRatePlan = foundPackage.productRatePlans.find((plan: any) => +plan.NVCompanySize__c === +selection.companySize);
            if (foundRatePlan) {
              totalPrice = foundRatePlan.productRatePlanCharges[0].pricing[0].price;
            }
          }
          newExtras.push({
            extraPrice,
            extraUnit: this.props.translate("EuroPerPerson"),
            features: p.features,
            price: totalPrice + basePrice,
            quantity: p.quantity,
            sku: p.sku,
            tooltipText: this.props.translate("PaymentsDesc"),
            unit: this.props.translate("Employees"),
          });
        }
      } else {
        newExtras.push({
          disabled: p.disabled,
          features: p.features,
          price: p.price,
          sku: p.sku
        });
      }
    });
    return newExtras;
  }

  private calculateExtraPrices(catalogData: any, selection: any) {
    const newExtras = Array();
    this.state.extraPackages.forEach((p) => {
      const foundPackage = catalogData.find((dataPackage: any) => dataPackage.sku === p.sku);
      if (foundPackage && foundPackage.productRatePlans && foundPackage.productRatePlans[0] && foundPackage.productRatePlans[0] && foundPackage.productRatePlans[0].productRatePlanCharges[0].pricing[0]) {
        const charge = foundPackage.productRatePlans[0].productRatePlanCharges[0];
        const pricing = (selection.product.sku === "NV-starter" && p.sku === "NV-pal" && charge.pricing[1]) || charge.pricing[0];
        const pricingTiers = pricing.tiers;
        let totalPrice = 0;
        let extraPrice = 0; // this is used to show the price per person
        const foundExtra = selection.extraServices.find((e: any) => e.sku === foundPackage.sku);
        let extraQuantity = 0;
        let accNumbers = 0;
        const tilFound = selection.extraServices.find((e: any) => e.sku === "NV-til");
        if (foundExtra) {
          if (p.sku === "NV-til" && tilFound.showQuantity !== undefined && tilFound.showQuantity > 0) {
            foundExtra.quantity = foundExtra.quantity / 21;

            tilFound.showQuantity = 0;
          }
          extraQuantity = foundExtra.quantity;
          accNumbers = extraQuantity * 21;
        }
        // if extra package "NV-pal" was not selected, but value was used in salary calculation trainings
        // read extraQuantity for package "NV-pal" from selected salary calculation training.
        else if (p.sku === "NV-pal") {
          const foundSelectedSalaryTraining = (selection.trainings || []).find((e: any) => this.state.salaryCalculationTrainingPackages.some((s: any) => s.sku === e.sku));
          if (foundSelectedSalaryTraining && foundSelectedSalaryTraining.quantity !== undefined) {
            extraQuantity = foundSelectedSalaryTraining.quantity;
          }
        }
        if (pricingTiers && p.sku === "NV-til") {
          pricingTiers.forEach((t: any) => {
            if (accNumbers > t.startingUnit && accNumbers < t.endingUnit) {
              totalPrice += t.price * accNumbers;
            } else if (accNumbers > t.startingUnit && totalPrice === 0 && t.endingUnit === null) {
              totalPrice += t.price * accNumbers;
            }
          });
          extraPrice = totalPrice / extraQuantity || 0;
        } else if (pricingTiers) {
          totalPrice = calculateTierPrice(charge.model, pricing, extraQuantity);
          const unitPrice = extraQuantity > 0 ? calculateTierUnitPrice(charge.model, pricing, extraQuantity) : undefined;
          extraPrice = unitPrice !== undefined ? unitPrice : totalPrice / extraQuantity || 0;
        } else {
          const foundRatePlan = foundPackage.productRatePlans.find((plan: any) => +plan.NVCompanySize__c === +selection.companySize);
          if (foundRatePlan) {
            totalPrice = foundRatePlan.productRatePlanCharges[0].pricing[0].price;
          }
        }
        let hidden = true;
        // hide or show some extras based on businessOrganization type
        if (foundPackage.sku === "NV-rb") {
          hidden = false;
        }
        if (selection.businessOrganization === 2 && foundPackage.sku === "NV-kons") {
          hidden = false;
        } else if (selection.businessOrganization === 3 && foundPackage.sku === "NV-cont") {
          hidden = false;
        }
        let disable = false;
        if ((selection.product.sku === "NV-nvpalkp" && foundPackage.sku === "NV-pal") || (selection.product.sku === "NV-nvpalkp" && foundPackage.sku === "NV-til")) {
          extraPrice = 0;
          disable = true;
          newExtras.push({
            disabled: disable,
            extraPrice,
            extraUnit: this.props.translate("EuroPerEmployee"),
            hidden,
            price: 0,
            quantity: 0,
            sku: foundPackage.sku,
            tooltipText: this.props.translate("ExtraTooltipPayroll"),
            type: "large",
            unit: this.props.translate("Employees"),
          });
        } else {
          newExtras.push({
            disabled: p.disabled,
            extraPrice,
            extraUnit: foundPackage.sku === "NV-mkl" ? this.props.translate("EuroPerPerson") : this.props.translate("EuroPerEmployee"),
            hidden,
            price: totalPrice,
            quantity: extraQuantity,
            sku: foundPackage.sku,
            tooltipText: foundPackage.sku === "NV-mkl" ? this.props.translate("ExtraTooltipTravel") : this.props.translate("ExtraTooltipPayroll"),
            type: foundPackage.sku === "NV-kons" || foundPackage.sku === "NV-rb" || foundPackage.sku === "NV-cont" ? "small" : "large",
            unit: foundPackage.sku === "NV-mkl" ? this.props.translate("Persons") : this.props.translate("Employees"),
          });
        }
      }
    });
    return newExtras;
  }

  private calculateTrainingPrices(catalogData: any, selection: any, sku: string) {
    const newTrainings = Array();
    this.state.trainingPackages.forEach((p) => {
      const foundPackage = catalogData.find((dataPackage: any) => dataPackage.sku === p.sku);
      if (foundPackage) {
        let price = 0;
        if (
          foundPackage.productRatePlans[0] &&
          foundPackage.productRatePlans[0].productRatePlanCharges[0] &&
          foundPackage.productRatePlans[0].productRatePlanCharges[0].pricing[0] &&
          foundPackage.productRatePlans[0].productRatePlanCharges[0].pricing[0].price
        ) {
          price = foundPackage.productRatePlans[0].productRatePlanCharges[0].pricing[0].price;
        }

        let hidden = p.hidden;
        let disabledTraining = false;
        // hide or show some extras based on businessOrganization type
        if (p.sku === "NV-kou5") {
          hidden = !(selection.extraServices || []).find((extra: any) => (extra.sku === "NV-kons" || extra.sku === "NV-rb") && extra.selected);
        } else if (p.sku === "NV-kou6") {
          hidden = !(selection.extraServices || []).find((extra: any) => extra.sku === "NV-cont" && extra.selected);
        }
        if (["NV-kou2", "NV-kou3"].includes(p.sku) && selection.product.sku === "NV-nvpalkp") {
          disabledTraining = true;
        }
        newTrainings.push({
          disabled: disabledTraining,
          features: p.features,
          hidden,
          price,
          sku: foundPackage.sku
        });
      }
    });
    return newTrainings;
  }

  private calculateSalaryCalculationTrainingPrices(catalogData: any, selection: any) {
    const newTrainings = Array();

    const salaryTrainingCount = ((selection.trainings || []).find((e: any) => this.state.salaryCalculationTrainingPackages.some((s: any) => e.sku === s.sku)) || { quantity: 0 }).quantity || 0;
    const extraServicesCount = ((selection.extraServices || []).find((e: any) => e.sku === "NV-pal") || { quantity: 0 }).quantity || 0;
    const employeeCount = Math.max(salaryTrainingCount, extraServicesCount);

    this.state.salaryCalculationTrainingPackages.forEach((p) => {
      const foundPackage = catalogData.find((dataPackage: any) => dataPackage.sku === p.sku);
      if (foundPackage) {
        let price = 0;
        let disabledTraining = false;
        if (
          foundPackage.productRatePlans[0] &&
          foundPackage.productRatePlans[0].productRatePlanCharges[0] &&
          foundPackage.productRatePlans[0].productRatePlanCharges[0].pricing[0] &&
          foundPackage.productRatePlans[0].productRatePlanCharges[0].pricing[0].price
        ) {
          price = foundPackage.productRatePlans[0].productRatePlanCharges[0].pricing[0].price;
        }
        if ((foundPackage.sku === "NV-palkou-basic" || foundPackage.sku === "NV-palkou-extended") && selection.product.sku !== "NV-nvpalkp") {
          disabledTraining = true;
        }
        // NV-palkou-basic and NV-palkou-extended are always visible; others use employee count range
        const isHidden = (foundPackage.sku === "NV-palkou-basic" || foundPackage.sku === "NV-palkou-extended")
          ? false
          : (selection ? !(employeeCount >= p.min && employeeCount <= (p.max || employeeCount)) : p.hidden);

        // push updated training
        newTrainings.push({
          disabled: disabledTraining,
          features: p.features,
          hidden: isHidden,
          max: p.max,
          min: p.min,
          price,
          sku: foundPackage.sku
        });
      }
    });

    // returns updated trainings
    return newTrainings;
  }

  private onCompanySizeChanged(value: number) {
    const offer = Object.assign({}, this.state.offer);
    const selection = Object.assign({}, offer.selection || {});
    selection.companySize = this.companySizeMap[value];

    const packages = this.calculatePackagePrices(this.state.catalogData, selection.companySize || 35, selection.extraServices);
    const newPackagePrices = this.calculateExtraPrices(this.state.catalogData, selection);
    offer.selection.extraServices.forEach((p) => {
      newPackagePrices.forEach((e) => {
        if ((e.sku === "NV-kons" || e.sku === "NV-rb") && e.sku === p.sku) {
          p.price = e.price;
        }
      });
    });

    // Deselect package if it was just disabled and we had it as selected
    if (!!packages.find((p: any) => p.disabled && p.sku === this.state.offer.selection.product.sku)) {
      selection.product = { sku: "" };
    }
    selection.extraServices = selection.extraServices.map((s: any) => (s.sku === "NV-cont" ? { ...s, selected: false } : s));

    offer.selection = selection;

    this.setState(
      {
        companySize: value,
        extraPackages: newPackagePrices,
        isSelectionValid: !!offer.selection.product.sku,
        offer,
        packages,
      },
      () => this.updatePrice$.next()
    );
  }

  private onAmountChanged(event: React.FormEvent<HTMLInputElement>) {
    if (event.currentTarget.dataset.property === "sales" || event.currentTarget.dataset.property === "purchases" || event.currentTarget.dataset.property === "accounts") {
      let quantity = +event.currentTarget.value;
      if (quantity < 0) {
        quantity = 0;
      } else if (quantity > +event.currentTarget.max) {
        quantity = +event.currentTarget.max;
      }
      const property = event.currentTarget.dataset.property;
      const offer = Object.assign({}, this.state.offer);
      const selection = Object.assign({}, offer.selection);
      if (event.currentTarget.dataset.property !== "accounts") {
        selection[property] = [
          { ...selection[property][0], quantity },
          { ...selection[property][1], quantity },
        ];
      } else {
        const tilIndex = selection.extraServices.findIndex((extra) => extra.sku === "NV-til");
        selection.extraServices[tilIndex].quantity = quantity;
        if (quantity > 0) {
          selection.extraServices[tilIndex].selected = true;
        }
      }
      this.addSalesAndPurchaseExtra(selection, property, quantity);
      offer.selection = selection;
      this.setState(
        {
          extraPackages: this.calculateExtraPrices(this.state.catalogData, selection),
          offer,
          packages: this.calculatePackagePrices(this.state.catalogData, selection.companySize || 35, selection.extraServices),
        },
        () => this.updatePrice$.next()
      );
    }
  }

  private handlePlusMinus(event: React.MouseEvent<HTMLButtonElement>) {
    // extract property and multiplier from event
    if (event.currentTarget.dataset.property === "sales" || event.currentTarget.dataset.property === "purchases" || event.currentTarget.dataset.property === "accounts") {
      const property = event.currentTarget.dataset.property;
      const multiplier = event.currentTarget.dataset.type === "plus" ? 1 : -1;
      const offer = Object.assign({}, this.state.offer);
      const selection = Object.assign({}, offer.selection);
      let quantity = 0;
      if (event.currentTarget.dataset.property !== "accounts") {
        quantity = this.state.offer.selection[property][0].quantity + multiplier * 1;
      } else {
        const tilIndex = selection.extraServices.findIndex((extra) => extra.sku === "NV-til");
        quantity = selection.extraServices[tilIndex].quantity || 0;
        quantity = quantity + multiplier * 1;
      }
      const max = event.currentTarget.parentElement ? (event.currentTarget.parentElement.parentElement ? (event.currentTarget.parentElement.parentElement.querySelector("input") || { max: 100000 }).max : 100000) : 100000;
      if (quantity < 0) {
        quantity = 0;
      } else if (quantity > +max) {
        quantity = +max;
      }
      if (quantity >= 0) {
        if (event.currentTarget.dataset.property !== "accounts") {
          selection[property] = [
            { ...selection[property][0], quantity },
            { ...selection[property][1], quantity },
          ];
        } else {
          const tilIndex = selection.extraServices.findIndex((extra) => extra.sku === "NV-til");
          selection.extraServices[tilIndex].quantity = quantity;
          if (quantity > 0) {
            selection.extraServices[tilIndex].selected = true;
          }
        }
        this.addSalesAndPurchaseExtra(selection, property, quantity);
        offer.selection = selection;
        this.setState(
          {
            extraPackages: this.calculateExtraPrices(this.state.catalogData, selection),
            offer,
            packages: this.calculatePackagePrices(this.state.catalogData, selection.companySize || 35, selection.extraServices, this.state.offer.selection.product.sku),
          },
          () => this.updatePrice$.next()
        );
      }
    }
  }

  private addSalesAndPurchaseExtra(selection: IOffer["selection"], property: string, quantity: number) {
    if (property === "sales") {
      const foundSalesExtraIdx = selection.extraServices.findIndex((extra) => extra.sku === "NV-tr-ml");
      if (foundSalesExtraIdx !== -1) {
        selection.extraServices[foundSalesExtraIdx].quantity = quantity;
        selection.extraServices[foundSalesExtraIdx].selected = selection.product.sku === "NV-basic" && quantity > 0;
      } else {
        selection.extraServices.push({
          quantity,
          selected: selection.product.sku === "NV-basic" && quantity > 0,
          sku: "NV-tr-ml",
        });
      }
    } else if (property === "purchases") {
      const foundPurchasesExtraIdx = selection.extraServices.findIndex((extra) => extra.sku === "NV-tr-ol");
      if (foundPurchasesExtraIdx !== -1) {
        selection.extraServices[foundPurchasesExtraIdx].quantity = quantity;
        selection.extraServices[foundPurchasesExtraIdx].selected = selection.product.sku === "NV-basic" && quantity > 0;
      } else {
        selection.extraServices.push({
          quantity,
          selected: selection.product.sku === "NV-basic" && quantity > 0,
          sku: "NV-tr-ol",
        });
      }
    } else if (property === "accounts") {
      const foundPurchasesExtraIdx = selection.extraServices.findIndex((extra) => extra.sku === "NV-til");
      if (foundPurchasesExtraIdx !== -1) {
        selection.extraServices[foundPurchasesExtraIdx].quantity = quantity;
        selection.extraServices[foundPurchasesExtraIdx].selected = quantity > 0;
      } else {
        selection.extraServices.push({
          quantity,
          selected: quantity > 0,
          sku: "NV-til",
        });
      }
    }
  }

  private businessOrganizationChanged(event: React.FormEvent<HTMLInputElement>) {
    const offer = Object.assign({}, this.state.offer);
    const selection = Object.assign({}, offer.selection || {});
    selection.businessOrganization = +event.currentTarget.value;
    offer.selection = selection;
    // Deselect extras that were specific to one businesstype
    selection.extraServices = selection.extraServices.map((s: any) => (s.sku === "NV-kons" || s.sku === "NV-cont" ? { ...s, selected: false, disabled: false } : s));
    this.setState(
      {
        extraPackages: this.calculateExtraPrices(this.state.catalogData, selection),
        offer,
        trainingPackages: this.calculateTrainingPrices(this.state.catalogData, selection, ""),
      },
      () => this.updatePrice$.next()
    );
  }

  private expirationDateChanged(value: any) {
    const offer = Object.assign({}, this.state.offer);
    const fixDate = new Date(value);
    fixDate.setTime(value.getTime() + (24 * 60 * 60 * 1000 - 1));
    offer.expirationUTC = fixDate;
    this.setState({ offer });
  }

  private handlePackageSelect(sku: string) {
    const offer = Object.assign({}, this.state.offer);
    const selection = Object.assign({}, offer.selection);

    const extras = this.state.extraPackages;
    // let isShowTooltip = false;
    // deselect if needed
    if (selection.product.sku !== sku) {
      if (selection.product.sku === "NV-nvpalkp" || sku === "NV-nvpalkp") {
        const salaryService = selection.extraServices.findIndex((extra) => extra.sku === "NV-pal");
        if (salaryService === -1) {
          selection.extraServices.push({
            price: 0,
            quantity: 0,
            selected: false,
            sku: "NV-pal",
          });
        } else {
          selection.extraServices[salaryService].selected = false;
          selection.extraServices[salaryService].quantity = 0;
        }
        selection.trainings = [];
        const tilService = selection.extraServices.findIndex((extra) => extra.sku === "NV-til");
        selection.extraServices[tilService].quantity = 0;
        selection.extraServices[tilService].price = 0;
        const tilExtra = extras.findIndex((extra) => extra.sku === "NV-til");
        extras[tilExtra].quantity = 0;
        extras[tilExtra].price = 0;
        selection.purchases[0].quantity = 0;
        selection.sales[0].quantity = 0;
        selection.purchases[1].quantity = 0;
        selection.sales[1].quantity = 0;
      }
      selection.product = { sku };
    } else {
      selection.product = { sku: "" };
      if (sku === "NV-nvpalkp") {
        const accService = selection.extraServices.findIndex((extra) => extra.sku === "NV-toi");
        if (accService !== -1) {
          selection.extraServices[accService].quantity = 0;
        }
        const salaryService = selection.extraServices.findIndex((extra) => extra.sku === "NV-pal");
        if (salaryService !== -1) {
          selection.extraServices[salaryService].quantity = 0;
        }
        const tilService = selection.extraServices.findIndex((extra) => extra.sku === "NV-til");
        selection.extraServices[tilService].quantity = 0;
        selection.extraServices[tilService].price = 0;
        const tilExtra = extras.findIndex((extra) => extra.sku === "NV-til");
        extras[tilExtra].quantity = 0;
        extras[tilExtra].price = 0;
        selection.purchases[0].quantity = 0;
        selection.sales[0].quantity = 0;
        selection.purchases[1].quantity = 0;
        selection.sales[1].quantity = 0;
        // Deselect salary training packages when NV-nvpalkp is deselected
        if (selection.trainings) {
          selection.trainings = selection.trainings.filter((t: any) =>
            !this.state.salaryCalculationTrainingPackages.some((s: any) => s.sku === t.sku)
          );
        }
      }
    }
    offer.selection = selection;

    // handle extras if selected basic package
    if (sku === "NV-basic") {
      const foundSalesExtraIdx = selection.extraServices.findIndex((extra) => extra.sku === "NV-tr-ml");
      if (foundSalesExtraIdx !== -1) {
        selection.extraServices[foundSalesExtraIdx].selected = !!selection.product.sku && selection.sales[0].quantity > 0;
        selection.extraServices[foundSalesExtraIdx].quantity = selection.sales[0].quantity;
      } else {
        selection.extraServices.push({
          quantity: selection.sales[0].quantity,
          selected: !!selection.product.sku && selection.sales[0].quantity > 0,
          sku: "NV-tr-ml",
        });
      }
      const foundPurchasesExtraIdx = selection.extraServices.findIndex((extra) => extra.sku === "NV-tr-ol");
      if (foundPurchasesExtraIdx !== -1) {
        selection.extraServices[foundPurchasesExtraIdx].selected = !!selection.product.sku && selection.purchases[0].quantity > 0;
        selection.extraServices[foundPurchasesExtraIdx].quantity = selection.purchases[0].quantity;
      } else {
        selection.extraServices.push({
          quantity: selection.purchases[0].quantity,
          selected: !!selection.product.sku && selection.purchases[0].quantity > 0,
          sku: "NV-tr-ol",
        });
      }
    } else {
      // Deselect all related extras if it's not basic
      const foundSalesExtraIdx = selection.extraServices.findIndex((extra) => extra.sku === "NV-tr-ml");
      if (foundSalesExtraIdx !== -1) {
        selection.extraServices[foundSalesExtraIdx].selected = false;
      }
      const foundPurchasesExtraIdx = selection.extraServices.findIndex((extra) => extra.sku === "NV-tr-ol");
      if (foundPurchasesExtraIdx !== -1) {
        selection.extraServices[foundPurchasesExtraIdx].selected = false;
      }
    }
    const salary = this.state.salaryCalculationTrainingPackages;
    const salaryPackage = extras.find((t: any) => t.sku === "NV-pal");
    const tilPackage = extras.find((t: any) => t.sku === "NV-til");
    const salaryOffer = selection.extraServices.findIndex((t: any) => t.sku === "NV-pal");
    if ((sku === "NV-nvpalkp") && salaryOffer !== -1) {
      salaryPackage.disabled = !salaryPackage.disabled;
      salaryPackage.quantity = 0;
      salaryPackage.selected = false;
      salaryPackage.price = 0;
      salaryPackage.extraPrice = 0;

      tilPackage.disabled = !tilPackage.disabled;
      selection.purchases[0].disabled = !selection.purchases[0].disabled;
      selection.sales[0].disabled = !selection.sales[0].disabled;

      selection.extraServices[salaryOffer].selected = false;
      const selectSalaryTraining = salary.findIndex((t: any) => t.sku === "NV-palkou-basic");
      const selectSalaryTraining6 = salary.findIndex((t: any) => t.sku === "NV-palkou-extended");
      if (selection.product.sku === "NV-nvpalkp") {
        if (selectSalaryTraining !== -1) { salary[selectSalaryTraining].disabled = false; }
        if (selectSalaryTraining6 !== -1) { salary[selectSalaryTraining6].disabled = false; }
      } else {
        if (selectSalaryTraining !== -1) { salary[selectSalaryTraining].disabled = true; }
        if (selectSalaryTraining6 !== -1) { salary[selectSalaryTraining6].disabled = true; }
      }
    }
    else if(sku === "NV-starter") {
      selection.extraServices.forEach((e) => {
        e.quantity = 0;
        e.price = 0;
        e.selected = false;
      });
      salary.forEach((e) => {
        e.disabled = true;
      });
      salaryPackage.disabled = false;
      selection.extraServices[salaryOffer].selected = true;
      selection.extraServices[salaryOffer].quantity = 1;
      salaryPackage.quantity = 1;
      salaryPackage.price = 0;

      selection.purchases.forEach((e) => {
        e.disabled = true;
        e.quantity = 0;
        e.price = 0;
      });

      selection.sales.forEach((e) => {
        e.disabled = true;
        e.quantity = 0;
        e.price = 0;
      });

      tilPackage.disabled = true;
      tilPackage.quantity = 0;
      tilPackage.selected = false;
      tilPackage.price = 0;
      tilPackage.extraPrice = 0;

      // NV-net (opening fee) is always included when NV-starter is selected
      const foundNetExtraIdx = selection.extraServices.findIndex((e: any) => e.sku === "NV-net");
      if (foundNetExtraIdx !== -1) {
        selection.extraServices[foundNetExtraIdx].selected = true;
      } else {
        selection.extraServices.push({ selected: true, sku: "NV-net" });
      }
    }
    else {
      salary.forEach((e) => {
        e.disabled = true;
      });
      salaryPackage.disabled = false;
      tilPackage.disabled = false;
      selection.purchases[0].disabled = false;
      selection.sales[0].disabled = false;
    }

    const foundRackBeatIdx = extras.find((t: any) => t.sku === "NV-rb");
    const foundRackBeatSelectionIdx = selection.extraServices.findIndex((extra) => extra.sku === "NV-rb");
    if (sku === "NV-basic" || sku === "NV-starter" || sku === "NV-core") {
      foundRackBeatIdx.disabled = true;
      if (foundRackBeatSelectionIdx !== -1) {
        selection.extraServices[foundRackBeatSelectionIdx].selected = false;
      }
    } else {
      if (foundRackBeatIdx !== -1) {
        foundRackBeatIdx.disabled = false;
      }
    }

    this.setState(
      {
        extraPackages: this.calculateExtraPrices(this.state.catalogData, selection),
        isLoadingPrice: true,
        isSelectionValid: !!offer.selection.product.sku,
        netvisorOpeningFee: sku === "NV-starter" ? true : this.state.netvisorOpeningFee,
        offer,
        packages: this.calculatePackagePrices(this.state.catalogData, selection.companySize || 35, selection.extraServices, sku),
        salaryCalculationTrainingPackages: salary,
        trainingPackages: this.calculateTrainingPrices(this.state.catalogData, selection, sku),
      },
      () => this.updatePrice$.next()
    );
  }

  private handleExtraSelect(sku: string) {
    const offer = Object.assign({}, this.state.offer);
    const selection = Object.assign({}, offer.selection);
    const foundExtraIdx = selection.extraServices.findIndex((s: any) => s.sku === sku);
    if (foundExtraIdx !== -1) {
      selection.extraServices[foundExtraIdx].selected = !selection.extraServices[foundExtraIdx].selected;
      if (sku === "NV-rb" || sku === "NV-kons") {
        selection.extraServices[foundExtraIdx].quantity = 1;
        const actualExtra = Object.assign({}, this.state);
        const foundActualExtra = actualExtra.extraPackages.find((s: any) => s.sku === sku);
        foundActualExtra.quantity = 1;
        selection.extraServices[foundExtraIdx].price = foundActualExtra.price;
        foundActualExtra.extraPrice = foundActualExtra.price;
      }
      if (sku === "NV-pal" && selection.product.sku !== "NV-starter") {
        // select/deselect internet paycheck
        const foundPaycheckIdx = selection.extraServices.findIndex((s: any) => s.sku === "NV-verk-p");
        if (foundPaycheckIdx !== -1) {
          selection.extraServices[foundPaycheckIdx].selected = selection.extraServices[foundExtraIdx].selected;
        } else {
          selection.extraServices.push({
            selected: selection.extraServices[foundExtraIdx].selected,
            sku: "NV-verk-p",
          });
        }
        const foundRepeatPaycheck = selection.extraServices.findIndex((s: any) => s.sku === "NV-toi");
        if (foundRepeatPaycheck !== -1) {
          selection.extraServices[foundRepeatPaycheck].selected = selection.extraServices[foundExtraIdx].selected;
        } else {
          selection.extraServices.push({
            selected: selection.extraServices[foundExtraIdx].selected,
            sku: "NV-toi",
          });
        }
        const foundRegNotification = selection.extraServices.findIndex((s: any) => s.sku === "NV-tri");
        if (foundRegNotification !== -1) {
          selection.extraServices[foundRegNotification].selected = selection.extraServices[foundExtraIdx].selected;
        } else {
          selection.extraServices.push({
            selected: selection.extraServices[foundExtraIdx].selected,
            sku: "NV-tri",
          });
        }
      }
    } else {
      selection.extraServices.push({
        quantity: 0,
        selected: true,
        sku,
      });
      if (sku === "NV-pal" && selection.product.sku !== "NV-starter") {
        selection.extraServices.push({
          quantity: 0,
          selected: true,
          sku: "NV-verk-p",
        });
        selection.extraServices.push({
          quantity: 0,
          selected: true,
          sku: "NV-toi",
        });
        selection.extraServices.push({
          quantity: 0,
          selected: true,
          sku: "NV-tri",
        });
      }
    }

    offer.selection = selection;

    this.setState(
      {
        offer,
        trainingPackages: this.calculateTrainingPrices(this.state.catalogData, this.state.offer.selection, ""),
      },
      () => this.updatePrice$.next()
    );
  }

  private handleTrainingSelect(sku: string) {
    const offer = { ...this.state.offer };
    const selection = { ...offer.selection };
    let trainings = [...(selection.trainings || [])];
    const foundTraining = trainings.find((t: any) => t.sku === sku);
    // remove selected training
    if (foundTraining) {
      trainings = trainings.filter((t: any) => t.sku !== sku);
      // add training
    } else {
      // if selected training is one of the salary calculation trainings, add employee count for it (from "NV-pal")
      if (this.state.salaryCalculationTrainingPackages.some((s: any) => s.sku === sku)) {
        const employeeCount = (selection.extraServices.find((e: any) => e.sku === "NV-pal") || { quantity: 0 }).quantity || 0;
        trainings.push({ sku, quantity: employeeCount });
      }
      // mark as selected
      else {
        trainings.push({ sku });
      }
    }
    selection.trainings = trainings;
    offer.selection = selection;
    this.setState({ offer }, () => this.updatePrice$.next());
  }

  private handleExtraQuantity(sku: string, quantity: number, selected?: boolean) {
    const offer = { ...this.state.offer };
    const selection = { ...offer.selection };
    let foundExtraIdx;
    if (sku === "NV-nvpalkp") {
      foundExtraIdx = this.state.packages.findIndex((s: any) => s.sku === sku);
      if (foundExtraIdx !== -1) {
        if (selected !== undefined || !this.state.isSelectionValid) {
          this.handlePackageSelect(sku);
          this.state.packages[foundExtraIdx].selected = selected;
          this.setState({ isLoadingPrice: false, isSelectionValid: true });
        }
        this.state.packages[foundExtraIdx].quantity = quantity;
        if (quantity > 0) {
          selection.product.sku = "NV-nvpalkp";
          this.state.price.usage.price = 0;
        } else {
          selection.product = { sku: "" };
          this.setState({
            isLoadingPrice: true,
            isSelectionValid: false,
          });
        }
      }
      foundExtraIdx = selection.extraServices.findIndex((s: any) => s.sku === "NV-pal");
      selection.extraServices[foundExtraIdx].selected = true;
    } else {
      foundExtraIdx = selection.extraServices.findIndex((s: any) => s.sku === sku);
    }
    if (foundExtraIdx !== -1) {
      selection.extraServices[foundExtraIdx].quantity = quantity;
      if (selected !== undefined) {
        selection.extraServices[foundExtraIdx].selected = selected;
      }
      if ((sku === "NV-pal" && selection.product.sku !== "NV-starter") || sku === "NV-nvpalkp") {
        // select/deselect internet paycheck
        const foundPaycheckIdx = selection.extraServices.findIndex((s: any) => s.sku === "NV-verk-p");
        if (foundPaycheckIdx !== -1) {
          selection.extraServices[foundPaycheckIdx].selected = selection.extraServices[foundExtraIdx].selected;
        } else {
          selection.extraServices.push({ selected: selection.extraServices[foundExtraIdx].selected, sku: "NV-verk-p" });
        }
        const foundRepeatPaycheck = selection.extraServices.findIndex((s: any) => s.sku === "NV-toi");
        if (foundRepeatPaycheck !== -1) {
          selection.extraServices[foundRepeatPaycheck].selected = selection.extraServices[foundExtraIdx].selected;
        } else {
          selection.extraServices.push({ selected: selection.extraServices[foundExtraIdx].selected, sku: "NV-toi" });
        }
        const foundRegNotification = selection.extraServices.findIndex((s: any) => s.sku === "NV-tri");
        if (foundRegNotification !== -1) {
          selection.extraServices[foundRegNotification].selected = selection.extraServices[foundExtraIdx].selected;
        } else {
          selection.extraServices.push({ selected: selection.extraServices[foundExtraIdx].selected, sku: "NV-tri" });
        }
      }
    } else {
      selection.extraServices.push({
        quantity,
        selected: selected !== undefined ? selected : true,
        sku,
      });
      if ((sku === "NV-pal" && selection.product.sku !== "NV-starter") || sku === "NV-nvpalkp") {
        selection.extraServices.push({
          quantity,
          selected: true,
          sku: "NV-verk-p",
        });
        selection.extraServices.push({
          quantity,
          selected: true,
          sku: "NV-toi",
        });
        selection.extraServices.push({
          quantity,
          selected: true,
          sku: "NV-tri",
        });
      }
    }

    // Extra rule to add internet paycheck if paycheck service is in use
    // handle quantity changes for
    if ((sku === "NV-pal" && selection.product.sku !== "NV-starter") || sku === "NV-nvpalkp") {
      const foundPaycheckIdx = selection.extraServices.findIndex((s: any) => s.sku === "NV-verk-p");
      if (foundPaycheckIdx !== -1) {
        selection.extraServices[foundPaycheckIdx].quantity = quantity;
      }
      const foundRepeatPaycheck = selection.extraServices.findIndex((s: any) => s.sku === "NV-toi");
      if (foundRepeatPaycheck !== -1) {
        selection.extraServices[foundRepeatPaycheck].quantity = quantity;
      }
      const foundRegNotification = selection.extraServices.findIndex((s: any) => s.sku === "NV-tri");
      if (foundRegNotification !== -1) {
        selection.extraServices[foundRegNotification].quantity = quantity;
      }
      this.handleEmployeeCountChanged(selection, quantity);
    }
    offer.selection = selection;
    if (sku !== "NV-nvpalkp") {
      this.setState(
        {
          extraPackages: this.calculateExtraPrices(this.state.catalogData, selection),
          offer,
          salaryCalculationTrainingPackages: this.updateVisibleSalaryCalculationTrainingPackagesByCount(quantity, sku),
        },
        () => this.updatePrice$.next()
      );
    } else {
      this.setState(
        {
          extraPackages: this.calculateExtraPrices(this.state.catalogData, selection),
          offer,
          packages: this.calculateSalaryPackagePrice(this.state.catalogData, selection),
          salaryCalculationTrainingPackages: this.updateVisibleSalaryCalculationTrainingPackagesByCount(quantity, sku),
        },
        () => this.updatePrice$.next()
      );
    }
  }

  private handleEmployeeCountChanged = (selection: any, count: number) => {
    // package was previously selected
    const selected = (selection.trainings || []).some((s: any) => this.state.salaryCalculationTrainingPackages.some((t: any) => t.sku === s.sku));

    // remove earlier selections
    if (selected) {
      // select all trainings which not found from salaryCalculationTrainingPackages array
      selection.trainings = selection.trainings.filter((s: any) => !this.state.salaryCalculationTrainingPackages.some((t: any) => t.sku === s.sku));
    }

    // find package which mathes to selected quantity
    const matchingPackage = (() => {
      // if nothing was selected -> return no package
      if (!selected) {
        return null;
      }

      // get mathing package
      let foundPackage = this.state.salaryCalculationTrainingPackages[0] || null;
      this.state.salaryCalculationTrainingPackages.forEach((e) => {
        if (count >= e.min && count <= (e.max || count)) {
          foundPackage = e;
          return;
        }
      });
      return foundPackage;
    })();

    // add selection if was selected
    // if quantity is 0 and training was previously selected and now quantity is 0 -> do not select package
    if (matchingPackage && count > 0) {
      selection.trainings.push({ name: matchingPackage.name, price: matchingPackage.name, selected: true, sku: matchingPackage.sku, quantity: count });
    }
  };

  private updateVisibleSalaryCalculationTrainingPackagesByCount = (quantity: number, sku: string) => {
    this.state.salaryCalculationTrainingPackages.forEach((e) => {
      if ((e.sku === "NV-palkou-basic" || e.sku === "NV-palkou-extended") && quantity > 0 && (sku === "NV-pal" || sku === "NV-nvpalkp")) {
        e.disabled = false;
      } else if ((e.sku === "NV-palkou-basic" || e.sku === "NV-palkou-extended") && quantity === 0 && (sku === "NV-pal" || sku === "NV-nvpalkp")) {
        e.disabled = true;
      }
      if (e.sku === "NV-palkou-basic" || e.sku === "NV-palkou-extended") {
        e.hidden = false;
      } else {
        const inRange = quantity >= e.min && quantity <= (e.max || quantity);
        e.hidden = !inRange;
        if (inRange && (sku === "NV-pal" || sku === "NV-nvpalkp")) {
          e.disabled = false;
        }
      }
    });
    return this.state.salaryCalculationTrainingPackages;
  };

  private updateVisibleSalaryCalculationTrainingPackagesBySelection = (selection: any) => {
    const salaryTrainingCount = ((selection.trainings || []).find((e: any) => this.state.salaryCalculationTrainingPackages.some((s: any) => e.sku === s.sku)) || { quantity: 0 }).quantity || 0;
    const extraServicesCount = ((selection.extraServices || []).find((e: any) => e.sku === "NV-pal") || { quantity: 0 }).quantity || 0;
    const employeeCount = Math.max(salaryTrainingCount, extraServicesCount);
    this.state.salaryCalculationTrainingPackages.forEach((e) => {
      if (e.sku === "NV-palkou-basic" || e.sku === "NV-palkou-extended") {
        e.hidden = false;
      } else {
        const inRange = employeeCount >= e.min && employeeCount <= (e.max || employeeCount);
        e.hidden = !inRange;
        if (inRange) {
          e.disabled = false;
        }
      }
    });
    return this.state.salaryCalculationTrainingPackages;
  };

  private handleContinueButton(event: React.FormEvent) {
    let currentDescr;

    currentDescr = this.description.current ? this.description.current.value : "";
    const newOffer = {
      ...this.state.offer,
      description: currentDescr,
    };
    this.props.updateAndExpandOffer(newOffer, this.state.catalogData, this.state.price);

    this.handleContinueAnalytics();
  }

  private handleContinueAnalytics() {
    if (process.env.REACT_APP_PRODUCTION === "true") {
      if (process.env.REACT_APP_FE_MODE === "internal") {
        // myynti.netvisor.fi
        ReactGA.event({
          action: "contacts",
          category: "myynti",
        });
      } else if (process.env.REACT_APP_FE_MODE === "embed") {
        // hinta.netvisor.fi
        ReactGA.event({
          action: "contacts",
          category: "verkko",
        });
      } else {
        // laskuri.netvisor.fi
        ReactGA.event({
          action: "contacts",
          category: "ao",
        });
      }
    }
  }

  private updatePrice(offers?: IOffer[], catalogData?: ICatalogItem[], currentToken?: string) {
    const defaultPrice = {
      oneTime: { price: 0 },
      price: 0,
      recurring: { price: 0 },
      usage: { price: 0 },
    };
    if (this.state.isSelectionValid) {
      if (offers && offers.length > 1 && catalogData) {
        offers.forEach((offer: IOffer) => {
          if (offer.token !== this.state.token) {
            this.api.getPrice(offer).subscribe((priceData: IPrice) => {
              offers.forEach((o) => (o = this.offerService.bundleOffer(o, catalogData, priceData)));
              this.props.updateOffers(offers, currentToken);
            });
          }
        });
      }
      this.api.getPrice(this.state.offer).subscribe(
        (priceData: IPrice) => {
          let openingFee = 0;
          if (priceData && priceData.oneTime && priceData.oneTime.products) {
            const foundOpeningFee = priceData.oneTime.products.find((p: any) => p.sku === "NV-net");
            if (foundOpeningFee) {
              openingFee = foundOpeningFee.price;
            }
          }
          // Sales and purchases prices
          const sales = (priceData.usage.products || []).filter((p) => p.sku === "NV-vll" || p.sku === "NV-vii").reduce((a, b) => (a += b.price), 0);
          const purchases = (priceData.usage.products || []).filter((p) => p.sku === "NV-vlv" || p.sku === "NV-mak").reduce((a, b) => (a += b.price), 0);
          const accounts = (priceData.usage.products || []).filter((p) => p.sku === "NV-til");
          if (accounts.length !== 0) {
            (priceData.usage.products || []).filter((p) => p.sku === "NV-til")[0].price = this.state.extraPackages[2].price;
          }
          if (this.state.offer.selection.product.sku === "NV-nvpalkp") {
            const salaryPrice = this.state.packages.find((e: any) => e.sku === "NV-nvpalkp").price;
            priceData.recurring.price = salaryPrice;
            if (priceData.usage.products !== undefined) {
              priceData.usage.products.forEach((a) => {
                if (a.sku === "NV-net") {
                  a.price = 0;
                }
              });
            }
          }
          this.setState(
            {
              isLoadingPrice: false,
              price: { ...defaultPrice, ...priceData, openingFee, sales, purchases },
            },
            () => this.updateTotalPrice()
          );
        },
        (error) => {
          console.error("price errored!", error);
          this.setState({
            error: true,
            errorMessage: error.toString(),
            isLoadingPrice: false,
            price: defaultPrice,
          });
        }
      );
    } else {
      this.setState({
        price: defaultPrice,
      });
    }
    this.updateRecommended();
  }
  private updateTooltip() {
    let isShowChangeProductTooltip = false;
    const currentSku = this.state.offer.selection.product.sku;
    if (this.state.isSelectionValid && currentSku !== "NV-prof" && currentSku !== "NV-nvpalkp" && currentSku !== "NV-starter") {
      const basicPackage = this.state.packages.find((p: any) => p.sku === "NV-basic");
      const corePackage = this.state.packages.find((p: any) => p.sku === "NV-core");
      if (basicPackage && corePackage && !basicPackage.disabled && !corePackage.disabled) {
        if (currentSku === "NV-basic" && corePackage.price < basicPackage.price) {
          isShowChangeProductTooltip = true;
          this.setState({ tooltipPackage: "NV-core" });
        }
        if (currentSku === "NV-core" && basicPackage.price < corePackage.price) {
          isShowChangeProductTooltip = true;
          this.setState({ tooltipPackage: "NV-basic" });
        }
      }
    }
    this.setState({
      changePackageTooltip: isShowChangeProductTooltip,
    });
  }

  private updateTotalPrice() {
    this.setState({
      totalPrice: this.calcTotalPrice(),
    });
    this.updateTooltip();
  }

  private updateRecommended() {
    let recommendedPackage;
    if (this.state.companySize < 2) {
      recommendedPackage = this.state.packages[0].sku;
    } else if (this.state.companySize < 4 && this.state.companySize > 1) {
      recommendedPackage = this.state.packages[2].sku;
    } else {
      recommendedPackage = this.state.packages[3].sku;
    }

    let recommendedTraining;
    if (this.state.offer.selection.product.sku === "NV-basic" || this.state.offer.selection.product.sku === "NV-starter" || this.state.offer.selection.product.sku === "NV-core") {
      recommendedTraining = this.state.trainingPackages[1].sku;
    } else if (this.state.offer.selection.product.sku === "NV-prof") {
      recommendedTraining = this.state.trainingPackages[2].sku;
    }

    this.setState({ recommendedPackage, recommendedTraining });
  }

  private toggleOfferDialog() {
    this.setState({ showOfferDialog: !this.state.showOfferDialog });
  }

  private handleExistingOffer() {
    this.setState({ showOfferDialog: true, showSuccessDialog: false, showWarningDialog: false });

    if (this.dialogSubscription) {
      this.dialogSubscription.unsubscribe();
    }

    this.dialogSubscription = this.dialogClicks$.subscribe(() => {
      this.setState({ showOfferDialog: false });

      let tokenToSearch = "";
      if (this.offerDialogToken.current) {
        tokenToSearch = this.offerDialogToken.current.value;
      }

      if (/^\w{8}-\w{4}-\w{4}-\w{4}-\w{12}$/.test(tokenToSearch)) {
        this.showWarning();
      } else {
        this.api.getOffers(tokenToSearch).subscribe(
          (offers: IOffer[]) => {
            const foundOffer = offers.find((offer: IOffer) => offer.token === tokenToSearch);
            if (foundOffer && foundOffer.token) {
              this.preprocessOffer(foundOffer);
              this.setState(
                {
                  companySize: this.companySizeMapReverse[foundOffer.selection.companySize],
                  extraPackages: this.calculateExtraPrices(this.state.catalogData, foundOffer.selection),
                  isSelectionValid: !!foundOffer.selection.product.sku,
                  netvisorOpeningFee: !!foundOffer.selection.extraServices.find((e: any) => e.sku === "NV-net"),
                  offer: foundOffer,
                  packages: this.calculatePackagePrices(this.state.catalogData, foundOffer.selection.companySize, foundOffer.selection.extraServices),
                  salaryCalculationTrainingPackages: this.updateVisibleSalaryCalculationTrainingPackagesBySelection(foundOffer.selection),
                  showSuccessDialog: true,
                },
                () => this.updatePrice$.next()
              );

              if (offers && offers.length > 1) {
                offers.forEach((offer: IOffer) => {
                  if (offer.token !== foundOffer.token) {
                    this.api.getPrice(offer).subscribe((priceData: IPrice) => {
                      offers.forEach((o) => (o = this.offerService.bundleOffer(o, this.state.catalogData, priceData)));
                      this.props.updateOffers(offers, foundOffer.token);
                    });
                  }
                });
              }
            } else {
              this.showWarning();
            }
          },
          () => this.showWarning()
        );
      }
    });
  }
  private showWarning() {
    this.setState({ showWarningDialog: true, showOfferDialog: false, showSuccessDialog: false });
  }
  private handleYes() {
    this.dialogClicks$.next();
  }
  private handleDialogEnter(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      this.dialogClicks$.next();
    }
  }
  private handleDialogClose() {
    this.setState({
      showOfferDialog: false,
      showSuccessDialog: false,
      showWarningDialog: false,
    });
  }

  private handleNetvisorOpeningFee() {
    const offer = Object.assign({}, this.state.offer);
    const selection = offer.selection;
    const foundOpeningFeeExtra = selection.extraServices.find((e: any) => e.sku === "NV-net");
    if (foundOpeningFeeExtra) {
      foundOpeningFeeExtra.selected = !this.state.netvisorOpeningFee;
    } else {
      selection.extraServices.push({
        selected: true,
        sku: "NV-net",
      });
    }
    this.setState({ netvisorOpeningFee: !this.state.netvisorOpeningFee }, () => this.updatePrice$.next());
  }

  private handleFocus(event: React.FocusEvent<HTMLInputElement>) {
    // Select all with a hack that works on all browsers
    const browser = DetectBrowser.detect();
    if (browser && browser.name === "safari") {
      event.target.setAttribute("type", "text");
      event.target.setSelectionRange(0, 9999);
      event.target.setAttribute("type", "number");
    } else {
      event.target.select();
    }
  }
}

export default withLocalize(Calculator);
